package dk.smark.chat.ui

import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.filled.ArrowBack
import androidx.compose.material.icons.filled.Add
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.unit.dp
import dk.smark.chat.FriendsViewModel
import dk.smark.chat.network.FriendRequest
import dk.smark.chat.network.User
import org.koin.compose.viewmodel.koinViewModel

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun FriendsScreen(
    userId: Int,
    onAddFriend: () -> Unit,
    onBack: () -> Unit,
    viewModel: FriendsViewModel = koinViewModel()
) {
    val friends by viewModel.friends.collectAsState()
    val pendingRequests by viewModel.pendingRequests.collectAsState()
    val isLoading by viewModel.isLoading.collectAsState()
    val error by viewModel.error.collectAsState()

    LaunchedEffect(userId) { viewModel.load(userId) }

    var selectedTab by remember { mutableIntStateOf(0) }
    val tabs = listOf("Friends", "Requests")

    Scaffold(
        topBar = {
            TopAppBar(
                title = { Text("Friends") },
                navigationIcon = {
                    IconButton(onClick = onBack) {
                        Icon(Icons.AutoMirrored.Filled.ArrowBack, contentDescription = "Back")
                    }
                },
                actions = {
                    IconButton(onClick = onAddFriend) {
                        Icon(Icons.Filled.Add, contentDescription = "Add Friend")
                    }
                }
            )
        }
    ) { innerPadding ->
        Column(modifier = Modifier.fillMaxSize().padding(innerPadding)) {
            TabRow(selectedTabIndex = selectedTab) {
                tabs.forEachIndexed { index, title ->
                    Tab(
                        selected = selectedTab == index,
                        onClick = { selectedTab = index },
                        text = { Text(title) }
                    )
                }
            }

            when {
                isLoading -> Box(Modifier.fillMaxSize(), contentAlignment = Alignment.Center) {
                    CircularProgressIndicator()
                }
                error != null -> Box(Modifier.fillMaxSize(), contentAlignment = Alignment.Center) {
                    Text(error!!, color = MaterialTheme.colorScheme.error)
                }
                selectedTab == 0 -> FriendsList(friends)
                else -> RequestsList(
                    requests = pendingRequests,
                    onAccept = { requestId -> viewModel.acceptRequest(requestId, userId) },
                    onDecline = { requestId -> viewModel.declineRequest(requestId, userId) }
                )
            }
        }
    }
}

@Composable
private fun FriendsList(friends: List<User>) {
    if (friends.isEmpty()) {
        Box(Modifier.fillMaxSize(), contentAlignment = Alignment.Center) {
            Text("No friends yet. Add some!", color = MaterialTheme.colorScheme.onSurfaceVariant)
        }
        return
    }
    LazyColumn(modifier = Modifier.fillMaxSize()) {
        items(friends) { user ->
            ListItem(
                headlineContent = { Text(user.name) },
                supportingContent = { Text(user.email) }
            )
            HorizontalDivider()
        }
    }
}

@Composable
private fun RequestsList(
    requests: List<FriendRequest>,
    onAccept: (Int) -> Unit,
    onDecline: (Int) -> Unit
) {
    if (requests.isEmpty()) {
        Box(Modifier.fillMaxSize(), contentAlignment = Alignment.Center) {
            Text("No pending requests.", color = MaterialTheme.colorScheme.onSurfaceVariant)
        }
        return
    }
    LazyColumn(modifier = Modifier.fillMaxSize()) {
        items(requests) { request ->
            ListItem(
                headlineContent = { Text(request.toEmail) },
                supportingContent = { Text("Pending") },
                trailingContent = {
                    Row(horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                        TextButton(onClick = { onAccept(request.id) }) { Text("Accept") }
                        TextButton(onClick = { onDecline(request.id) }) { Text("Decline") }
                    }
                }
            )
            HorizontalDivider()
        }
    }
}
